import { firstValueFrom, map, Observable, shareReplay } from 'rxjs';
import { Injectable } from '@angular/core';
import {
    ApolloBeginNcesSchoolImportGQL,
    ApolloGetImportedSchoolGQL,
    ApolloGetImportedSchoolsCountGQL,
    ApolloGetNcesSchoolUploadUrlGQL,
    ApolloSearchImportedSchoolsGQL,
    handleMutationResult,
    handleQueryResult,
} from '@lib/frontend-shared-data-gql-operations';
import { HttpClient } from '@angular/common/http';
import { ImportedSchool, SchoolSearchCriteria } from '@lib/shared-interface-school';

@Injectable({ providedIn: 'root' })
export class ImportedSchoolsService {
    public readonly schoolCount$: Observable<number> = this.getImportedSchoolsCountGql
        .watch()
        .valueChanges.pipe(
            map((result) => result.data.importedSchoolsCount),
            shareReplay(1),
        );

    public constructor(
        private readonly beginNcesSchoolImportGql: ApolloBeginNcesSchoolImportGQL,
        private readonly getImportedSchoolGql: ApolloGetImportedSchoolGQL,
        private readonly getImportedSchoolsCountGql: ApolloGetImportedSchoolsCountGQL,
        private readonly getNcesSchoolUploadUrlGql: ApolloGetNcesSchoolUploadUrlGQL,
        private readonly http: HttpClient,
        private readonly searchImportedSchoolsGql: ApolloSearchImportedSchoolsGQL,
    ) {}

    public getUploadUrl(): Promise<string> {
        // Don't cache an upload URL is only signed with temporary access.
        const result$ = this.getNcesSchoolUploadUrlGql.fetch({}, { fetchPolicy: 'no-cache' });
        const url$ = result$.pipe(
            handleQueryResult(),
            map((data) => data.getNcesSchoolUploadUrl),
        );

        return firstValueFrom(url$);
    }

    public beginImport(): Promise<boolean> {
        // Eventually, we may want to re-fetch schools once import is completed.
        // We'll probably need websockets for that.
        const result$ = this.beginNcesSchoolImportGql.mutate();
        const importStarted$ = result$.pipe(
            handleMutationResult(),
            map((data) => data.beginNcesSchoolImport),
        );

        return firstValueFrom(importStarted$);
    }

    public uploadFile(url: string, file: File): Observable<void> {
        // S3 signed URLs have to use the PUT http method (not POST)
        return this.http.put<void>(url, file);
    }

    public findBySchoolId(schoolId: string): Observable<ImportedSchool | undefined> {
        const query = this.getImportedSchoolGql.watch({ schoolId });
        return query.valueChanges.pipe(
            handleQueryResult(),
            map((data) => data.importedSchool),
        );
    }

    public search(
        searchCriteria: SchoolSearchCriteria,
        skipExistingCustomers?: boolean,
    ): Observable<ImportedSchool[]> {
        const query = this.searchImportedSchoolsGql.watch({
            searchCriteria,
            skipExistingCustomers,
        });
        return query.valueChanges.pipe(
            handleQueryResult(),
            map((data) => data.importedSchools),
        );
    }
}
